import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger';

// Fetch activities with pagination & filters
export const fetchActivities = createAsyncThunk(
  'activity/fetchActivities',
  async ({ page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Fetching activities with page:', page, 'and limit:', limit);
      const response = await api.activities.getAll({ page, limit, filters });
      if (response.data.status) {
        log.info('Activities fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch activities:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching activities:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching activities');
    }
  }
);

// Search activities with keyword
export const searchActivities = createAsyncThunk(
  'activity/searchActivities',
  async ({ keyword, page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Searching activities with keyword:', keyword);
      const response = await api.activities.search({ keyword, page, limit, ...filters });
      if (response.data.status) {
        log.info('Activities search completed successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to search activities:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error searching activities:', error);
      return rejectWithValue(error.response?.data?.message || 'Error searching activities');
    }
  }
);

// Create activity
export const createActivity = createAsyncThunk(
  'activity/createActivity',
  async (formData, { rejectWithValue }) => {
    try {
      log.info('Creating activity with data:', formData);
      const response = await api.activities.create(formData);
      if (response.data.status) {
        log.info('Activity created successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to create activity:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error creating activity:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create activity');
    }
  }
);

// Update activity
export const updateActivity = createAsyncThunk(
  'activity/updateActivity',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      log.info('Updating activity with data:', formData);
      const response = await api.activities.update(id, formData);
      if (response.data.status) {
        log.info('Activity updated successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to update activity:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating activity:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to update activity');
    }
  }
);

// Delete activity
export const deleteActivity = createAsyncThunk(
  'activity/deleteActivity',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting activity with ID:', id);
      const response = await api.activities.delete(id);
      if (response.data.status) {
        log.info('Activity deleted successfully:', id);
        return id;
      } else {
        log.warn('Failed to delete activity:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting activity:', error);
      return rejectWithValue(error.response?.data?.message || 'Error deleting activity');
    }
  }
);

// Bulk delete activities
export const bulkDeleteActivities = createAsyncThunk(
  'activity/bulkDeleteActivities',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.activities.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting activities');
    }
  }
);

// Bulk activate/deactivate activities
export const bulkUpdateActivityStatus = createAsyncThunk(
  'activity/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      await api.activities.bulkUpdateStatus(ids, status);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error updating activity status');
    }
  }
);

// Fetch a single activity by ID
export const fetchActivityById = createAsyncThunk(
  'activity/fetchActivityById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching activity with ID:', id);
      const response = await api.activities.getById(id);
      if (response.data.status) {
        log.info('Activity fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch activity:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching activity:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching activity');
    }
  }
);

export const updateActivityOrders = createAsyncThunk(
  'activity/updateOrders',
  async (activities) => {
    try {
      const response = await api.activities.updateOrders(activities);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update activity orders';
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  page: 1,
  pages: 0,
  limit: 10,
  loading: false,
  error: null
};

const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {
    reorderActivities: (state, action) => {
      const { oldIndex, newIndex } = action.payload;
      const [movedItem] = state.items.splice(oldIndex, 1);
      state.items.splice(newIndex, 0, movedItem);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch activities
      .addCase(fetchActivities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.activities || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(fetchActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search activities
      .addCase(searchActivities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.activities || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(searchActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create activity
      .addCase(createActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createActivity.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update activity
      .addCase(updateActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateActivity.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete activity
      .addCase(deleteActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteActivity.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload);
      })
      .addCase(deleteActivity.rejected, (state) => {
        state.loading = false;
      })
      // Bulk delete activities
      .addCase(bulkDeleteActivities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteActivities.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteActivities.rejected, (state) => {
        state.loading = false;
      })
      // Bulk update activity status
      .addCase(bulkUpdateActivityStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateActivityStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateActivityStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateActivityOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateActivityOrders.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateActivityOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export default activitySlice.reducer; 